import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import type { ITootBreadcrumbProps } from "./TootBreadcrumb.props";

export const TootBreadcrumb = ({
  ariaLabel,
  className,
  items,
  label = "Breadcrumb",
  separator = "/",
  variant = "short",
}: ITootBreadcrumbProps) => {
  const Link = resolveTootPrimitive("Link");
  const hasCurrentItem = items.some((item) => item.current);

  if (!items.length) {
    return null;
  }

  return (
    <nav
      aria-label={ariaLabel ?? label}
      className={cn(
        "toot-heat-panel inline-flex max-w-full min-w-0 items-center gap-3 text-toot-muted align-top max-sm:gap-2",
        variant === "deep"
          ? "grid w-full grid-cols-[auto_minmax(0,1fr)] items-stretch gap-[clamp(0.75rem,2vw,1rem)] p-[clamp(0.85rem,2vw,1.15rem)] max-sm:block max-sm:p-[0.58rem]"
          : "px-[0.65rem] py-[0.58rem] max-sm:p-[0.58rem]",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex min-h-11 flex-none items-center rounded-[var(--radius-toot-panel)] border border-toot-orange/40 bg-toot-surface-raised/80 px-[0.72rem] py-[0.55rem] text-[0.64rem] font-black uppercase leading-none tracking-[0.18em] text-toot-orange max-sm:hidden",
          variant === "deep" && "min-h-0 px-[0.9rem]",
        )}
      >
        Path
      </span>
      <ol
        className={cn(
          "m-0 flex min-w-0 flex-1 list-none flex-wrap items-center gap-y-2 p-0 text-[0.6875rem] font-[850] uppercase leading-[1.2] tracking-[0.15em] text-inherit max-sm:text-[0.64rem] max-sm:tracking-[0.11em]",
          variant === "deep" &&
            "relative content-center justify-stretch gap-x-[0.45rem] gap-y-[0.7rem] py-[0.1rem] max-sm:grid max-sm:grid-cols-1 max-sm:gap-[0.45rem]",
        )}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isCurrent = item.current || (!hasCurrentItem && isLast);
          const isDisabled = item.disabled || isCurrent;
          const isUnavailable = item.disabled && !isCurrent;
          const labelContent = (
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
              {item.text}
            </span>
          );

          return (
            <li
              className={cn(
                "inline-flex min-w-0 items-center",
                variant === "deep" &&
                  "relative z-[1] flex-[1_1_9.75rem] max-sm:grid max-sm:grid-cols-[auto_minmax(0,1fr)] max-sm:items-stretch max-sm:gap-[0.45rem] max-sm:first:grid-cols-[minmax(0,1fr)]",
              )}
              key={`${item.href}-${item.text}-${index}`}
            >
              {index > 0 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    "mx-[0.55rem] flex-none font-black text-[color-mix(in_oklab,var(--color-toot-orange)_76%,transparent)] max-sm:mx-[0.38rem]",
                    variant === "deep" &&
                      "max-sm:m-0 max-sm:grid max-sm:min-w-[1.85rem] max-sm:place-items-center max-sm:border max-sm:border-toot-orange/35 max-sm:bg-toot-surface-terminal/70",
                  )}
                >
                  {separator}
                </span>
              ) : null}
              {isDisabled ? (
                <span
                  aria-current={isCurrent ? "page" : undefined}
                  aria-disabled={isUnavailable ? true : undefined}
                  className={cn(
                    "inline-flex min-h-11 min-w-0 max-w-[min(18rem,70vw)] items-center justify-center overflow-hidden rounded-[var(--radius-toot-panel)] border border-transparent px-[0.88rem] py-[0.65rem] text-ellipsis whitespace-nowrap no-underline transition-[background,border-color,color,transform] duration-200 ease-out focus-visible:outline-offset-2 max-sm:max-w-[min(15rem,calc(100vw-3.75rem))] max-sm:px-[0.7rem] motion-reduce:transition-none",
                    variant === "deep" &&
                      "w-full max-w-none justify-start px-[clamp(0.7rem,1.5vw,0.95rem)] max-sm:px-[0.72rem]",
                    isCurrent
                      ? "border-toot-orange/60 bg-toot-red/15 text-toot-foreground"
                      : "border-toot-grid-strong/70 bg-toot-surface-terminal/85 text-toot-muted",
                    isUnavailable && "cursor-not-allowed text-toot-dim",
                  )}
                  title={item.text}
                >
                  {labelContent}
                </span>
              ) : (
                <Link
                  className={cn(
                    "inline-flex min-h-11 min-w-0 max-w-[min(18rem,70vw)] items-center justify-center overflow-hidden rounded-[var(--radius-toot-panel)] border border-toot-grid-strong/70 bg-toot-surface-terminal/85 px-[0.88rem] py-[0.65rem] text-ellipsis whitespace-nowrap text-toot-muted no-underline transition-[background,border-color,color,transform] duration-200 ease-out hover:-translate-y-px hover:border-toot-orange/60 hover:bg-toot-red/10 hover:text-toot-foreground focus-visible:outline-offset-2 max-sm:max-w-[min(15rem,calc(100vw-3.75rem))] max-sm:px-[0.7rem] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
                    variant === "deep" &&
                      "w-full max-w-none justify-start px-[clamp(0.7rem,1.5vw,0.95rem)] max-sm:px-[0.72rem]",
                  )}
                  aria-label={item.ariaLabel}
                  href={item.href}
                  rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
                  target={item.target}
                  title={item.text}
                >
                  {labelContent}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

TootBreadcrumb.displayName = "Navigation::TootBreadcrumb";
